import React, { useEffect, useRef } from 'react';
import { Box } from '@mantine/core';

interface FloatingElementsProps {
  count?: number;
  type?: 'code' | 'symbols' | 'geometric';
}

export const FloatingElements: React.FC<FloatingElementsProps> = ({ 
  count = 15, 
  type = 'code' 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const elements = {
    code: ['<>', '{}', '[]', '()', '=>', '&&', '||', '==', '!=', '++', '--', '//', '/*', '*/', 'fn', 'var', 'let', 'const'],
    symbols: ['★', '✦', '◆', '▲', '●', '♦', '▼', '◇', '◯', '△', '✧', '⬟', '⬢', '⬡', '◉', '◈'],
    geometric: ['▢', '▣', '▤', '▥', '▦', '▧', '▨', '▩', '■', '□', '▪', '▫', '▬', '◼', '◻', '◾']
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Create floating elements
    for (let i = 0; i < count; i++) {
      const element = document.createElement('div');
      const text = elements[type][Math.floor(Math.random() * elements[type].length)];
      
      const size = Math.random() * 20 + 12;
      const x = Math.random() * window.innerWidth;
      const y = Math.random() * window.innerHeight;
      const hue = 200 + Math.random() * 80;
      const duration = 8 + Math.random() * 12;
      const delay = Math.random() * 5;
      
      element.textContent = text;
      element.style.cssText = `
        position: fixed;
        left: ${x}px;
        top: ${y}px;
        font-size: ${size}px;
        color: hsl(${hue}, 60%, 70%);
        font-family: 'Fira Code', 'JetBrains Mono', monospace;
        font-weight: 400;
        pointer-events: none;
        z-index: 2;
        opacity: 0.3;
        animation: floatElement ${duration}s ease-in-out infinite;
        animation-delay: ${delay}s;
        text-shadow: 0 0 10px hsl(${hue}, 60%, 50%);
        user-select: none;
      `;
      
      container.appendChild(element);
    }

    return () => {
      // Cleanup
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, [count, type]);

  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 2,
          overflow: 'hidden'
        }}
      />
      
      <style>{`
        @keyframes floatElement {
          0%, 100% {
            transform: translateY(0px) translateX(0px) rotate(0deg);
            opacity: 0.2;
          }
          25% {
            transform: translateY(-20px) translateX(10px) rotate(5deg);
            opacity: 0.4;
          }
          50% {
            transform: translateY(-10px) translateX(-15px) rotate(-3deg);
            opacity: 0.6;
          }
          75% {
            transform: translateY(-30px) translateX(5px) rotate(2deg);
            opacity: 0.3;
          }
        }
      `}</style>
    </>
  );
};

// Orbital elements that rotate around the hero text
interface OrbitalElementsProps {
  centerX?: number;
  centerY?: number;
  radius?: number;
  count?: number;
}

export const OrbitalElements: React.FC<OrbitalElementsProps> = ({
  centerX = 50,
  centerY = 50,
  radius = 200,
  count = 8
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    for (let i = 0; i < count; i++) {
      const element = document.createElement('div');
      const angle = (360 / count) * i;
      const size = Math.random() * 8 + 4;
      const duration = 20 + Math.random() * 10;
      const hue = 200 + Math.random() * 60;
      
      element.style.cssText = `
        position: absolute;
        width: ${size}px;
        height: ${size}px;
        background: radial-gradient(circle, hsl(${hue}, 80%, 60%), transparent);
        border-radius: 50%;
        left: 50%;
        top: 50%;
        transform-origin: 0 0;
        animation: orbit ${duration}s linear infinite;
        animation-delay: ${i * 0.5}s;
        box-shadow: 0 0 ${size * 2}px hsl(${hue}, 80%, 60%);
      `;
      
      // Set initial position
      const x = Math.cos((angle * Math.PI) / 180) * radius;
      const y = Math.sin((angle * Math.PI) / 180) * radius;
      element.style.transform = `translate(${x}px, ${y}px)`;
      
      container.appendChild(element);
    }

    return () => {
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, [centerX, centerY, radius, count]);

  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: 'absolute',
          left: `${centerX}%`,
          top: `${centerY}%`,
          pointerEvents: 'none',
          zIndex: 1
        }}
      />
      
      <style>{`
        @keyframes orbit {
          from {
            transform: rotate(0deg) translateX(${radius}px) rotate(0deg);
          }
          to {
            transform: rotate(360deg) translateX(${radius}px) rotate(-360deg);
          }
        }
      `}</style>
    </>
  );
};

export default FloatingElements; 